import {GraphQLFormattedError} from 'graphql';
import {
  InMemoryCache,
  ApolloClient,
  createHttpLink,
  ApolloLink,
  Operation,
  type NormalizedCacheObject,
} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {RetryLink} from '@apollo/client/link/retry';
import {onError} from '@apollo/client/link/error';

import {allClientsSchemaExtension} from './query/AllClients.gql';
import {allStaffSchemaExtension} from './query/AllStaff.gql';
import {chatSchemaExtension} from './query/Chat.gql';
import {followUpsSchemaExtension} from './query/FollowUpThreads.gql';
import {initCache} from './cache/init';
import * as log from '../util/log';

// Expose things for debugging.
export const _debug = {
  cache: null as InMemoryCache | null,
  client: null as ApolloClient<NormalizedCacheObject> | null,
};

/**
 * OAuth2 access and refresh token information.
 */
export type AuthTokenInfo = {
  /**
   * Timestamp (milliseconds) of when the token will expires, based on the
   * client computer's time.
   */
  expiresAt: number;
  /**
   * OAuth2 access token (might be an empty string).
   */
  accessToken: string;
  /**
   * Type of the access token, usualy "Bearer".
   */
  accessTokenType: string;
  /**
   * OAuth2 refresh token, if it was provided by the server.
   */
  refreshToken?: string;
};

/**
 * Options to configure the Apollo client.
 */
export type ApolloClientConfigOpts = {
  /**
   * Get the URI for the GraphQL endpoint.
   */
  getBaseUri(): string;
  /**
   * Get all the OAuth2 token / refresh / expiration information
   */
  getAuthTokenInfo(): AuthTokenInfo;
  /**
   * Take care of network errors.
   */
  handleNetworkError(e: Error): void;
  /**
   * Take care of Authorization errors.
   */
  handleAuthError(e: Error): void;
  /**
   * Refresh the OAuth2 access token.
   */
  refreshAuthToken(): Promise<AuthTokenInfo>;
  /**
   * Handle other types of GraphQL errors.
   *
   * Returns `true` if the error is handled, `false` if not.
   */
  handleGraphqlError(e: GraphQLFormattedError, operation: Operation): boolean;
};

/**
 * Get the token as a string to use in the Authorization header.
 *
 * If the access token is missing this returns undefined.
 */
const authTokenString = (tokenInfo: AuthTokenInfo) => {
  if (!tokenInfo.accessToken) {
    return undefined;
  }

  return `${tokenInfo.accessTokenType} ${tokenInfo.accessToken}`;
};

/**
 * Inspect error to catch authentication issues.
 */
const tokenExpiredError = (e: GraphQLFormattedError) => {
  if (/^(Invalid access token|No access token)$/i.test(e.message)) {
    return true;
  }

  return false;
};

/*
/**
 * Get a configured instance of the Apollo Client.
 *
 * This client connects to the right graphql endpoint using the correct
 * authentication and handles errors.
 */
export const configureApolloClient = (opts: ApolloClientConfigOpts) => {
  log.debug('Configuring new Apollo Client ...');
  // Reference to a refresh promise so that only one happens at a time.
  let _refreshTokenRequestSingleton: Promise<AuthTokenInfo | void> | null =
    null;

  // Get the base URI for making requests.
  const httpLink = createHttpLink({
    uri: opts.getBaseUri,
  });

  // Add auth info to the request if it's available.
  const authLink = setContext(async (_, {headers}) => {
    if (_refreshTokenRequestSingleton) {
      await _refreshTokenRequestSingleton;
    }

    const tokenInfo = opts.getAuthTokenInfo();
    return {
      headers: {
        ...headers,
        authorization: authTokenString(tokenInfo),
      },
    };
  });

  // Retry requests periodically.
  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 10000,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error) => !!error,
    },
  });

  // Handle errors from GraphQL.
  const errorLink = onError(
    ({graphQLErrors, networkError, operation, response, forward}) => {
      if (graphQLErrors) {
        for (const e of graphQLErrors) {
          // Detect authentication errors.
          // TODO(jnu): handle insufficient permission errors separately
          if (tokenExpiredError(e)) {
            // Refresh the token and retry the operation.
            if (!_refreshTokenRequestSingleton) {
              _refreshTokenRequestSingleton = opts
                .refreshAuthToken()
                .catch((e2) => opts.handleAuthError(e2))
                .finally(() => {
                  _refreshTokenRequestSingleton = null;
                });
            }
            return forward(operation);
          }
        }

        // Filter errors based on custom handler
        if (response) {
          response.errors = response?.errors?.filter(
            (e) => !opts.handleGraphqlError(e, operation),
          );
          if (response.errors?.length === 0) {
            response.errors = undefined;
          }
        }
      }
      if (networkError) {
        opts.handleNetworkError(networkError);
      }
    },
  );

  // Pass the cache a functor that lets it get a reference to the client.
  const cache = initCache();
  const apolloClient = new ApolloClient({
    link: ApolloLink.from([errorLink, retryLink, authLink, httpLink]),
    typeDefs: [
      allClientsSchemaExtension,
      allStaffSchemaExtension,
      chatSchemaExtension,
      followUpsSchemaExtension,
    ].join('\n'),
    cache,
  });

  // Expose stuff for debugging
  _debug.cache = cache;
  _debug.client = apolloClient;

  // Ta-da! a quite fancy Apollo Client instance.
  return apolloClient;
};
